import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import designIllustration from '../assets/images/5.png';
import ProposalForm from "./ProposalForm";

const BRAND_BLUE = "#0047ff";
const BRAND_GRADIENT = "linear-gradient(135deg, #0047ff, #00c4ff)";

const Wrapper = styled.div`
  font-family: 'Poppins', sans-serif;
  min-height: 100vh;
  color: #e5e7eb;
  padding: 80px 20px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
  position: relative;
`;

const Hero = styled(motion.section)`
  text-align: center;
  padding: 40px 20px;

  @media (max-width: 360px) {
    padding: 30px 14px;  
  }
`;

const HeroTitle = styled(motion.h1)`
  max-width: 860px;

  font-size: clamp(2.1rem, 5vw, 4rem);

  line-height: 1.15;

  font-weight: 700;

  margin-bottom: 22px;

  color: white;

  letter-spacing: -1px;
`;

const HeroText = styled.p`
  max-width: 800px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const CtaButton = styled(motion.button)`
  margin-top: 30px;
  padding: 14px 36px;
  border-radius: 50px;
  border: none;
  background: ${BRAND_GRADIENT};
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
  cursor: pointer;
`;

const WhySection = styled.section`
  display: flex;
  flex-direction: column;   
  align-items: center;
  text-align: center;
  gap: 24px;
  max-width: 900px;
  width: 100%;
  margin: 60px auto;
  padding: 0 16px;
`;

const WhyText = styled.div`
  max-width: 700px;
`;

const WhyTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 16px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const WhyPara = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: #cbd5e1;
`;

const WhyImage = styled.img`
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 20px;
  display: block;
  margin: 0 auto;
`;

const FeaturesGrid = styled.section`
  display: flex;
  flex-wrap: wrap;
  gap: 30px;
  justify-content: center;
  padding-top: 60px;
`;

const FeatureBox = styled(motion.div)`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(14px);
  border-radius: 18px;
  padding: 26px;
  max-width: 320px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.12);
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 10px;
  background: ${BRAND_GRADIENT};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #cbd5e1;
`;

export default function GraphicDesign() {

  const [showProposal, setShowProposal] = useState(false);

  return (
    <>
      <Wrapper>
        <Hero
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: 'easeOut' }}
        >
          <HeroTitle>Creative Graphic Designing Company</HeroTitle>
          <HeroText>
            Transform your brand identity with our professional graphic designing services. 
            We create visually stunning, strategic, and impactful designs that communicate 
            your brand story effectively. From logos to marketing creatives, we craft 
            designs that captivate, convert, and build lasting impressions.
          </HeroText>

          <CtaButton
            whileHover={{ scale: 1.08, boxShadow: '0 0 16px rgba(0,86,210,.3)' }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            onClick={() => setShowProposal(true)}
          >
            Request a Proposal
          </CtaButton>
        </Hero>

        <WhySection>
          <WhyText>
            <WhyTitle>Why Invest In Graphic Design?</WhyTitle>
            <WhyPara>
              Strong visuals build strong brands. Graphic design enhances credibility, 
              increases engagement, and improves customer trust. From social media creatives 
              to brand identity systems, strategic design ensures your business stands out 
              in a competitive digital world.
            </WhyPara>
          </WhyText>

          <WhyImage
            src={designIllustration}
            alt="Graphic Design Illustration"
          />
        </WhySection>

        <FeaturesGrid>
          {features.map((feature) => (
            <FeatureBox
              key={feature.title}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{
                y: -12,
                scale: 1.05,
                boxShadow: "0 0 35px rgba(0,196,255,0.35)",
              }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDesc>{feature.description}</FeatureDesc>
            </FeatureBox>
          ))}
        </FeaturesGrid>
      </Wrapper>

      <ProposalForm open={showProposal} setOpen={setShowProposal} />
    </>
  );
}

const features = [
  {
    title: 'Logo & Brand Identity Design',
    description:
      'We create memorable logos and cohesive brand identity systems that reflect your vision and values.',
  },
  {
    title: 'Social Media Creatives',
    description:
      'Engaging social media graphics designed to increase visibility, engagement, and brand consistency.',
  },
  {
    title: 'Marketing & Advertising Designs',
    description:
      'Posters, banners, brochures, and ad creatives that drive attention and conversions.',
  },
  {
    title: 'UI Graphics & Visual Assets',
    description:
      'High-quality visual elements, icons, and illustrations tailored for digital platforms.',
  },
  {
    title: 'Packaging Design',
    description:
      'Creative packaging concepts that attract customers and strengthen product appeal.',
  },
  {
    title: 'Rebranding & Visual Refresh',
    description:
      'Modernize your brand visuals to align with evolving trends and customer expectations.',
  },
];